import { del, get, set } from "idb-keyval";
import { useSyncExternalStore } from "react";
import { create } from "zustand";
import { ApiClient, defaultApiBase } from "./api/client";
import type { CommandError } from "./api/types";
import { type CommandFailure, type EngineStatus, type Persistence, type Snapshot, SyncEngine } from "./sync/engine";
import { emptyState, type SyncState } from "./sync/state";

export interface StoredSession {
  apiBase: string;
  token: string;
  userId: string;
  email?: string;
  /** "login" sessions are revoked on logout via /user/logout; "token" sessions are just forgotten. */
  kind: "login" | "token";
}

const SESSION_KEY = "ld.session";

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function saveSession(s: StoredSession | null) {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

const cacheKey = (s: StoredSession) => `ld.sync.v1.${new URL(s.apiBase).host}.${s.userId}`;

function idbPersistence(key: string): Persistence {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latest: Snapshot | null = null;
  return {
    load: async () => (await get<Snapshot>(key)) ?? null,
    save: async (snap) => {
      latest = snap;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (latest) void set(key, latest).catch(() => undefined);
      }, 300);
    },
  };
}

export interface Toast {
  id: number;
  message: string;
  action?: { label: string; run: () => void };
  kind?: "error" | "info";
}

interface AppState {
  session: StoredSession | null;
  client: ApiClient;
  engine: SyncEngine | null;
  status: EngineStatus;
  toasts: Toast[];
  sidebarOpen: boolean;
  quickAdd: { open: boolean; defaults?: QuickAddDefaults };
  searchOpen: boolean;
  settingsOpen: string | null;
  shortcutsOpen: boolean;
  focusedTaskId: string | null;
  setSession(s: StoredSession): Promise<void>;
  logout(): Promise<void>;
  toast(t: Omit<Toast, "id">): void;
  dismissToast(id: number): void;
  set(
    partial: Partial<
      Pick<AppState, "sidebarOpen" | "quickAdd" | "searchOpen" | "settingsOpen" | "shortcutsOpen" | "focusedTaskId">
    >,
  ): void;
}

export interface QuickAddDefaults {
  projectId?: string;
  sectionId?: string | null;
  parentId?: string;
  dueString?: string;
  label?: string;
}

let toastSeq = 0;

function describeError(e: CommandError): string {
  return e.error || e.error_tag || `Error ${e.error_code}`;
}

function makeEngine(session: StoredSession, client: ApiClient): SyncEngine {
  return new SyncEngine(
    { sync: (token, commands) => client.sync(token, commands) },
    idbPersistence(cacheKey(session)),
    {
      onFailure: (failures: CommandFailure[]) => {
        const first = failures[0];
        if (first) useApp.getState().toast({ kind: "error", message: `⚠ ${describeError(first.error)}` });
      },
      onStatus: (status) => {
        useApp.setState({ status });
        if (status === "unauthorized") void useApp.getState().logout();
      },
    },
  );
}

const initialSession = loadSession();
const initialClient = new ApiClient(initialSession?.apiBase ?? defaultApiBase(), initialSession?.token ?? null);

export const useApp = create<AppState>((setState, getState) => ({
  session: initialSession,
  client: initialClient,
  engine: initialSession ? makeEngine(initialSession, initialClient) : null,
  status: "idle",
  toasts: [],
  sidebarOpen: typeof window === "undefined" || window.innerWidth >= 750,
  quickAdd: { open: false },
  searchOpen: false,
  settingsOpen: null,
  shortcutsOpen: false,
  focusedTaskId: null,

  async setSession(s) {
    saveSession(s);
    const client = new ApiClient(s.apiBase, s.token);
    setState({ session: s, client, engine: makeEngine(s, client) });
  },

  async logout() {
    const { session, client } = getState();
    if (session?.kind === "login") {
      await client.post("/user/logout").catch(() => undefined);
    }
    if (session) await del(cacheKey(session)).catch(() => undefined);
    saveSession(null);
    setState({ session: null, engine: null, client: new ApiClient(session?.apiBase ?? defaultApiBase(), null) });
  },

  toast(t) {
    const id = ++toastSeq;
    setState({ toasts: [...getState().toasts, { ...t, id }] });
    setTimeout(() => getState().dismissToast(id), t.action ? 6000 : 4000);
  },
  dismissToast(id) {
    setState({ toasts: getState().toasts.filter((x) => x.id !== id) });
  },
  set(partial) {
    setState(partial);
  },
}));

// Test hook (E2E waits for the command queue to drain); harmless in production.
if (typeof window !== "undefined") {
  (window as unknown as { __ldPending: () => number }).__ldPending = () => {
    const e = useApp.getState().engine;
    return e ? e.pendingCount + (e.status === "syncing" ? 1 : 0) : 0;
  };
}

export function useEngine(): SyncEngine {
  const engine = useApp((s) => s.engine);
  if (!engine) throw new Error("not logged in");
  return engine;
}

const EMPTY = emptyState();

/** The whole local store (re-renders on any change; derive with useMemo). */
export function useSyncState(): SyncState {
  const engine = useApp((s) => s.engine);
  return useSyncExternalStore(
    (fn) => engine?.subscribe(fn) ?? (() => undefined),
    () => engine?.state ?? EMPTY,
  );
}
